import React, { useState } from "react";
import {
  Card,
  CardContent,
  Grid,
  Typography,
  IconButton,
  Box,
  Button,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
// import VisibilityIcon from "@mui/icons-material/Visibility"; // (unused)
import { API_BASE_URL, IMAGE_BASE_URL, PICKUP_NAME, SELLER_GSTIN } from "../../config/constants";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

const statusLabels = {
  SC: "Payment Completed Sales",
  SF: "Payment Failed or Rejected",
  SP: "Payment Initiated and Ignored",
};

// Use the imported constant; do NOT re-read from process.env in the browser
const API_ROOT = API_BASE_URL.replace(/\/$/, ""); // strip trailing slash if any

export default function SalesTable({
  sales,
  handleEdit,
  handleViewClick,
  itemsList,
  onShipmentCreated, // optional: parent can pass a refresh callback
}) {
  const [creatingId, setCreatingId] = useState(null);

  const sortedSales = [...sales].sort(
    (a, b) => new Date(b.systemDate) - new Date(a.systemDate)
  );
  const groupedSales = { SC: [], SF: [], SP: [] };

  sortedSales.forEach((sale) => {
    if (groupedSales[sale.salesStatus]) {
      groupedSales[sale.salesStatus].push(sale);
    }
  });

  const getItemDetails = (sale) => {
    if (!sale?.items || !itemsList) return [];
    return Object.keys(sale.items)
      .map((itemId) => {
        const item = itemsList.find((i) => String(i.inventoryid) === String(itemId));
        return item
          ? {
            ...item,
            quantity: sale.items[itemId][0],
            price: sale.items[itemId][1],
            discount: sale.items[itemId][2],
          }
          : null;
      })
      .filter(Boolean);
  };

  // Delhivery disallows &, #, %, ;, \
  const dlvClean = (v) => (v == null ? "" : String(v).replace(/[&#%\\;]/g, " ").trim());

  const getProductsDesc = (sale, itemsList) => {
    try {
      const names = Object.keys(sale.items || {}).map((id) => {
        const item = itemsList?.find((i) => String(i.inventoryid) === String(id));
        return item?.label || id;
      });
      return dlvClean(names.join(", ").slice(0, 180) || "Jewellery");
    } catch {
      return "Jewellery";
    }
  };

  const getQuantitySum = (sale) => {
    try {
      return String(
        Object.values(sale.items || {}).reduce((sum, arr) => sum + (Number(arr?.[0]) || 0), 0) || 1
      );
    } catch {
      return "1";
    }
  };

  const createShipment = async (sale) => {
    try {
      setCreatingId(sale.id);

      // Decide payment mode from sale (adjust if you have a better flag)
      const paymentMode =
        sale?.salesType?.toUpperCase() === "COD" ? "COD" : "Prepaid";

      // Build from real sale fields
      const body = {
        orderId: `ILL-${sale.id}`,                 // MUST be unique
        paymentMode,
        pickupLocationName: PICKUP_NAME,           // from constants
        boxes: [
          {
            // Consignee (customer)
            name: dlvClean(sale.name || "Customer"),
            phone: String(sale.phoneNumber || "").replace(/\D/g, "").slice(-10) || "9999999999",
            add: dlvClean(sale.buyerDetails || ""),    // your address string
            pin: String(sale.pincode || ""),           // keep as string
            city: dlvClean(sale.city || sale.state || ""), // if you store city elsewhere, map it here
            state: dlvClean(sale.state || ""),
            country: "India",
            seller_gst_tin: SELLER_GSTIN,
            // Recommended fields
            products_desc: "Artificial Jewellery",
            quantity: "1",
            order_date: new Date().toISOString().slice(0, 10), // YYYY-MM-DD
            seller_name: "Anjana Asok",
            seller_add: "Flat No:9A , Artech Lake View apartments , Akkulam , Trivandrum 695029",

            // Parcel info (tweak if you store dimensions/weight)
            weight: "100",                 // grams
            shipment_width: "10",
            shipment_height: "5",
            shipment_length: "15",
            shipping_mode: "Surface",
            address_type: "home",

            // Amounts
            total_amount: String(sale.price || 0),
            cod_amount: paymentMode === "COD" ? String(sale.price || 0) : "0",
          },
        ],
      };

      // 1) Create Delhivery shipment
      const createRes = await fetch(`${API_ROOT}/shipping/delhivery/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const raw = await createRes.text();
      let createData;
      try { createData = JSON.parse(raw); }
      catch { throw new Error(`Non-JSON response (${createRes.status}): ${raw.slice(0, 200)}`); }

      if (!createRes.ok || !createData.ok) {
        const msg = createData?.error?.message || createData?.delhivery?.rmk || JSON.stringify(createData?.error) || "Delhivery create failed";
        throw new Error(msg);
      }

      // Extract waybill
      const waybill =
        createData?.delhivery?.waybill ||
        createData?.delhivery?.packages?.[0]?.waybill ||
        createData?.delhivery?.package?.waybill;

      if (!waybill) throw new Error("No waybill returned by Delhivery");

      // 2) Persist tracking to your backend and mark shipped (SD)
      /*
      const updateRes = await fetch(`${API_ROOT}/sales/${sale.id}/shipment`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          shipmentPrice: 0,
          shipmentMethod: "Delhivery",
          trackingId: waybill,
        }),
      });
      if (!updateRes.ok) {
        const errText = await updateRes.text();
        console.warn("Shipment persisted but status update failed:", errText);
      }
*/
      alert(`Shipment created! Waybill: ${waybill}`);
      if (typeof onShipmentCreated === "function") onShipmentCreated();
    } catch (e) {
      console.error("Create shipment error:", e);
      alert(`Delhivery error: ${e.message}`);
    } finally {
      setCreatingId(null);
    }
  };


  const sortedStatuses = ["SC", "SF", "SP"];

  return (
    <Box>
      {sortedStatuses.map((statusKey) => {
        const group = groupedSales[statusKey];
        if (group.length === 0) return null;

        return (
          <Box key={statusKey} sx={{ marginBottom: 4 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                marginBottom: 2,
                backgroundColor: "#f5f5f5",
                padding: "10px 15px",
                borderRadius: 2,
              }}
            >
              {statusLabels[statusKey]}
            </Typography>
            <Grid container spacing={2}>
              {group.map((sale, index) => {
                const isCompleted = sale.salesStatus === "SC";
                const isFailed = sale.salesStatus === "SF";

                const cardColor = isCompleted
                  ? "#C8E6C9"
                  : isFailed
                    ? "#FFE0B2"
                    : "#FFF9C4";

                const badgeColor = isCompleted
                  ? "#66BB6A"
                  : isFailed
                    ? "#FFA726"
                    : "#FFCA28";

                const itemDetails = getItemDetails(sale);
                const hasWaybill = !!sale.trackingId;

                return (
                  <Grid item xs={12} sm={6} md={6} key={sale.id}>
                    <Card
                      sx={{
                        borderRadius: 2,
                        boxShadow: 2,
                        backgroundColor: cardColor,
                      }}
                    >
                      <CardContent>
                        <Grid container spacing={2}>
                          {/* Left Column: Info */}
                          <Grid item xs={7}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mb: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 25,
                                  height: 25,
                                  backgroundColor: badgeColor,
                                  color: "black",
                                  borderRadius: "50%",
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  fontWeight: "bold",
                                  fontSize: "14px",
                                  boxShadow: 1,
                                }}
                              >
                                {index + 1}
                              </Box>
                            </Box>
                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                              {sale.name}
                            </Typography>
                            <Typography variant="body2" gutterBottom>
                              {sale.buyerDetails}
                              <br />
                              {sale.state} - {sale.pincode}
                              <br />
                              <Box
                                component="span"
                                sx={{
                                  backgroundColor: "white",
                                  color: "black",
                                  padding: "2px 8px",
                                  borderRadius: "6px",
                                  display: "inline-block",
                                  fontWeight: "bold",
                                  mt: 1,
                                }}
                              >
                                {sale.upiIdLastFour}
                              </Box>
                            </Typography>

                            <Typography
                              component="a"
                              href={`https://wa.me/91${sale.phoneNumber
                                }?text=${encodeURIComponent("Hi, Your order has been shipped.")}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                fontWeight: "bold",
                                color: "#25D366",
                                cursor: "pointer",
                                textDecoration: "none",
                                display: "flex",
                                alignItems: "center",
                                gap: "5px",
                                mt: 1,
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <WhatsAppIcon sx={{ fontSize: 18 }} /> {sale.phoneNumber}
                            </Typography>

                            <Typography fontWeight="bold" gutterBottom>
                              ₹{sale.price}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontStyle: "italic", color: "#333" }}
                              gutterBottom
                            >
                              <strong>Time:</strong>{" "}
                              {dayjs(sale.systemDate).utc().format("DD-MM-YY hh:mm A")}
                            </Typography>

                            {/* Create Shipment / Track Buttons */}
                            <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
                              {isCompleted && !hasWaybill && (
                                <Tooltip title="Create Delhivery Shipment">
                                  <span>
                                    <Button
                                      size="small"
                                      variant="contained"
                                      startIcon={
                                        creatingId === sale.id ? (
                                          <CircularProgress size={16} />
                                        ) : (
                                          <LocalShippingIcon />
                                        )
                                      }
                                      disabled={creatingId === sale.id}
                                      onClick={() => createShipment(sale)}
                                    >
                                      {creatingId === sale.id ? "Creating…" : "Create Shipment"}
                                    </Button>
                                  </span>
                                </Tooltip>
                              )}

                              {hasWaybill && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  href={`https://www.delhivery.com/track/package/${sale.trackingId}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  Track: {sale.trackingId}
                                </Button>
                              )}
                            </Box>
                          </Grid>

                          {/* Right Column: Images & Edit */}
                          <Grid item xs={5}>
                            {itemDetails.length > 0 && (
                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 1,
                                  mb: 2,
                                }}
                              >
                                {itemDetails.map((item, idx) => (
                                  <Box key={idx} sx={{ position: "relative" }}>
                                    <img
                                      src={IMAGE_BASE_URL + item.image}
                                      alt={item.label || "Item"}
                                      style={{
                                        width: "80px",
                                        height: "80px",
                                        objectFit: "cover",
                                        borderRadius: 4,
                                      }}
                                    />
                                    <Box
                                      sx={{
                                        position: "absolute",
                                        top: 2,
                                        right: 4,
                                        backgroundColor: "rgba(0,0,0,0.6)",
                                        color: "white",
                                        fontSize: "10px",
                                        padding: "1px 4px",
                                        borderRadius: "4px",
                                      }}
                                    >
                                      {item.boxno}
                                    </Box>
                                  </Box>
                                ))}
                              </Box>
                            )}
                            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                              <IconButton onClick={() => handleEdit(sale.id)} color="primary">
                                <EditIcon />
                              </IconButton>
                            </Box>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        );
      })}
    </Box>
  );
}
